/**
 * Voice QA corpus — capture what the automatic recogniser heard.
 *
 * Wherever `lib/speech.ts:recognizeOnce` judges an utterance, this module
 * keeps the audio and hands it, once the recogniser has answered, to the
 * offline queue in `lib/voice-qa-queue.ts`. Nothing identifying goes with it —
 * the sidecar carries the target word, the transcript, the verdict and the
 * platform, never a patient, run, item or device id. Design and threat model:
 * docs/voice-qa-corpus.md.
 *
 * Where it runs — decided once by {@link pickSurface}:
 *
 *   - patient app (native)                  → `patient-asr`
 *   - therapist web, browser recogniser     → `therapist-asr` — but NOT on a
 *     WebKit browser: on iPadOS Safari an open getUserMedia track silences the
 *     Web Speech recogniser (WebKit bug 321436, confirmed 2026-10-09), so
 *     capturing there would break the very thing it measures.
 *   - therapist web, phoneme engine         → nothing here. The clip already
 *     reaches our proxy and is teed server-side.
 *   - a Videosprechstunde dock              → nothing. The shell owns the mic.
 *
 * How the audio is obtained — decided once by {@link pickCaptureEngine}:
 *
 *   - `tee` (iOS): the recogniser's own plugin copies the buffers it feeds
 *     SFSpeechRecognizer and hands them back after the session
 *     (`lib/speech.ts:captureNextNativeUtterance`). One microphone consumer,
 *     and the clip is byte for byte what was judged — no pre-roll, because
 *     the recogniser had none either. None of the contention guards below
 *     apply; there is nothing to contend with.
 *   - `parallel` (Android, therapist web): neither the Android plugin nor the
 *     Web Speech API exposes audio, so the utterance is recorded *alongside*
 *     the recogniser through `lib/audio-recorder.ts` (the continuous
 *     ring-buffer engine the phoneme path and self-assessment already use)
 *     and cut once the recogniser has answered.
 *
 *   iOS used to run `parallel` too, and it broke recognition outright: WKWebView's
 *   capture and the plugin's AVAudioEngine share one AVAudioSession, the
 *   session change stops the engine silently, and every tap was scored as a
 *   miss (staging build 261013112, 2026-10-14). Do not reintroduce a second
 *   microphone consumer next to the native recogniser on iOS.
 *
 * On the `parallel` engine three guards keep that second microphone consumer
 * from costing a patient their drill, because the mic is contended (a
 * concurrent-capture policy on Android where the loser gets silence; a
 * browser's own arbitration on the web):
 *
 *   1. the device probe ({@link decideProbe}) — the first recognition with
 *      capture live decides, once per device per app/OS version, whether the
 *      two can coexist. "Unsafe" is remembered and capture never runs again on
 *      that device until a version changes.
 *   2. a running guard — two consecutive non-`ok` recognitions in a session
 *      with capture live switch it off for the rest of the session.
 *   3. silence rejection — a clip whose RMS says we never had the mic is
 *      dropped, and after a few of them the mic is closed so the next tap
 *      re-opens it from scratch.
 *
 * And one rule above all: nothing in here may delay the verdict, set a banner
 * or throw into the drill. {@link beginCapture} resolves `null` on any doubt,
 * and {@link CaptureHandle.finish} is fire-and-forget.
 *
 * Three switches, all default-off: `VITE_VOICE_QA` compiles the path in,
 * the server's `GET …/voice-qa` says whether this surface is wanted right now
 * (cached for offline launches), and the probe/session guards above.
 */

import { Device } from '@capacitor/device'
import { Preferences } from '@capacitor/preferences'

import { api } from './api'
import {
  closeMic,
  openMic,
  startRecording,
  stopRecording,
  type RecordedClip,
} from './audio-recorder'
import { logWarn } from './log'
import { isAndroid, isDocked, isIOS, isNative, isWebKitBrowser } from './platform'
import {
  captureNextNativeUtterance,
  nativeAudioTeeAvailable,
  type RecognitionResult,
} from './speech'
import { usePhonemeEngine } from './speech-engine'
import { enqueueSample, installSampleQueue, purgeSamples, randomId, type SampleMeta } from './voice-qa-queue'

export type VoiceQaSurface = 'patient-asr' | 'therapist-asr'

/**
 * Build-time switch. Folded to a literal `false` when the env is unset, so a
 * bundle built without it drops every branch below.
 */
export const voiceQaBuildEnabled: boolean =
  import.meta.env.VITE_VOICE_QA === '1' || import.meta.env.VITE_VOICE_QA === 'true'

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev'

const CONFIG_KEY = 'lingotalk.voice_qa.config'
const PROBE_KEY = 'lingotalk.voice_qa.probe'

/**
 * RMS below which a clip is treated as "no audio". Speech at a normal
 * distance from a tablet mic sits around 0.03–0.1 RMS in float samples; a
 * silent room with the mic live is ~0.001–0.005. A capture that lost the
 * microphone is exactly 0.
 */
export const RMS_FLOOR = 0.008

/** Consecutive silent clips before the mic is closed and re-opened on the next tap. */
const SILENT_CLIPS_BEFORE_REOPEN = 3

/** Consecutive non-`ok` recognitions with capture live before the session gives up. */
const FAILURES_BEFORE_SESSION_OFF = 2

const DEV = import.meta.env.DEV
function devLog(message: string, ...args: unknown[]): void {
  if (DEV) console.log(`[voice-qa] ${message}`, ...args)
}

// --- pure decisions (exported for tests) ------------------------------------

export interface SurfaceEnv {
  buildEnabled: boolean
  docked: boolean
  phonemeEngine: boolean
  native: boolean
  webKitBrowser: boolean
}

/**
 * Which surface this runtime captures for, or `null` for none. See the module
 * doc for the reasoning behind each exclusion.
 */
export function pickSurface(env: SurfaceEnv): VoiceQaSurface | null {
  if (!env.buildEnabled) return null
  if (env.docked) return null
  if (env.phonemeEngine) return null
  if (env.native) return 'patient-asr'
  if (env.webKitBrowser) return null
  return 'therapist-asr'
}

/** See the module doc: `tee` takes the recogniser's input, `parallel` records next to it. */
export type CaptureEngine = 'tee' | 'parallel'

/**
 * How a surface gets its audio. The native tee is the only option where it
 * exists; everything else records in parallel.
 */
export function pickCaptureEngine(env: {
  surface: VoiceQaSurface
  nativeTee: boolean
}): CaptureEngine {
  return env.surface === 'patient-asr' && env.nativeTee ? 'tee' : 'parallel'
}

export type ProbeVerdict = 'safe' | 'unsafe' | 'inconclusive'

/**
 * Did the recogniser still work with our capture live?
 *
 *   - it failed outright                      → unsafe
 *   - it heard the word                       → safe
 *   - it heard nothing but we recorded voice  → unsafe (we took the mic)
 *   - it heard nothing and so did we          → inconclusive: nobody spoke;
 *                                               decide on the next attempt
 */
export function decideProbe(input: {
  status: RecognitionResult['status']
  transcript: string
  rms: number | null
}): ProbeVerdict {
  if (input.status !== 'ok') return 'unsafe'
  if (input.transcript.trim().length > 0) return 'safe'
  if (input.rms === null) return 'inconclusive'
  return input.rms >= RMS_FLOOR ? 'unsafe' : 'inconclusive'
}

/**
 * Did this recognition fail *with capture live*, for the running guard on the
 * parallel engine? A non-`ok` status obviously. But the failure that
 * contention actually produces is quieter: the recogniser times out hearing
 * nothing and reports `ok` with an empty transcript — while our clip has
 * voice on it. Counting only statuses let that run for a whole session.
 */
export function recognitionFailedWithCapture(
  result: { status: RecognitionResult['status']; transcript: string },
  rms: number | null,
): boolean {
  if (result.status !== 'ok') return true
  return result.transcript.trim().length === 0 && rms !== null && rms >= RMS_FLOOR
}

export interface WavStats {
  rms: number
  durationMs: number
  sampleRate: number
  channels: number
}

/**
 * RMS + duration of a 16-bit PCM WAV, read straight off the bytes. Returns
 * `null` for anything that isn't one (the one-shot MediaRecorder fallback
 * produces webm/mp4, which we can't inspect without decoding) — such clips
 * skip the silence check rather than being dropped.
 */
export function analyseWav(buffer: ArrayBuffer): WavStats | null {
  if (buffer.byteLength < 44) return null
  const view = new DataView(buffer)
  const tag = (at: number): string =>
    String.fromCharCode(view.getUint8(at), view.getUint8(at + 1), view.getUint8(at + 2), view.getUint8(at + 3))
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') return null

  // Walk the chunks: `fmt ` gives the layout, `data` the samples. Our own
  // encoder writes exactly these two at fixed offsets, but a WAV from
  // elsewhere may carry extra chunks in between.
  let offset = 12
  let channels = 1
  let sampleRate = 16000
  let bits = 16
  let dataStart = -1
  let dataLength = 0
  while (offset + 8 <= buffer.byteLength) {
    const id = tag(offset)
    const size = view.getUint32(offset + 4, true)
    if (id === 'fmt ') {
      channels = view.getUint16(offset + 10, true) || 1
      sampleRate = view.getUint32(offset + 12, true) || 16000
      bits = view.getUint16(offset + 22, true) || 16
    } else if (id === 'data') {
      dataStart = offset + 8
      dataLength = Math.min(size, buffer.byteLength - dataStart)
      break
    }
    offset += 8 + size + (size % 2)
  }
  if (dataStart < 0 || bits !== 16) return null

  const samples = Math.floor(dataLength / 2)
  let sumSq = 0
  for (let i = 0; i < samples; i++) {
    const s = view.getInt16(dataStart + i * 2, true) / 32768
    sumSq += s * s
  }
  const rms = samples === 0 ? 0 : Math.sqrt(sumSq / samples)
  const frames = samples / channels
  return { rms, durationMs: Math.round((frames / sampleRate) * 1000), sampleRate, channels }
}

// --- runtime state -----------------------------------------------------------

const surface: VoiceQaSurface | null = pickSurface({
  buildEnabled: voiceQaBuildEnabled,
  docked: isDocked,
  phonemeEngine: usePhonemeEngine,
  native: isNative,
  webKitBrowser: isWebKitBrowser,
})

const engine: CaptureEngine | null =
  surface === null ? null : pickCaptureEngine({ surface, nativeTee: nativeAudioTeeAvailable })

interface RemoteConfig {
  enabled: boolean
}

/** `null` until the server (or the on-disk cache) has answered. */
let config: RemoteConfig | null = null
let configInFlight: Promise<void> | null = null

interface ProbeRecord {
  /** `${app version}|${platform}|${os version}` — a change re-probes. */
  key: string
  safe: boolean
}

/** `undefined` = not loaded yet, `null` = no verdict for the current key. */
let probe: ProbeRecord | null | undefined
let probeKey: string | null = null

interface ClientInfo {
  platform: string
  os_major: string
  app_version: string
  device_model?: string
}
let client: ClientInfo | null = null

let installed = false
let sessionDisabled = false
let consecutiveFailures = 0
let consecutiveSilent = 0

function engineName(): string {
  if (isIOS) return 'ios-sfspeechrecognizer'
  if (isAndroid) return 'android-speechrecognizer'
  return 'web-speech'
}

function configUrl(): string {
  return isNative ? '/patient/voice-qa' : '/therapist/voice-qa'
}

async function loadClientInfo(): Promise<ClientInfo> {
  if (client !== null) return client
  let info: ClientInfo = { platform: isNative ? (isIOS ? 'ios' : 'android') : 'web', os_major: '', app_version: APP_VERSION }
  try {
    const d = await Device.getInfo()
    info = {
      platform: d.platform,
      os_major: String(d.osVersion ?? '').split('.')[0] ?? '',
      app_version: APP_VERSION,
      ...(isNative && d.model ? { device_model: d.model } : {}),
    }
  } catch {
    /* keep the fallback */
  }
  client = info
  probeKey = `${info.app_version}|${info.platform}|${info.os_major}`
  return info
}

async function loadProbe(): Promise<void> {
  if (probe !== undefined) return
  await loadClientInfo()
  try {
    const { value } = await Preferences.get({ key: PROBE_KEY })
    const parsed = value ? (JSON.parse(value) as Partial<ProbeRecord>) : null
    probe =
      parsed !== null && parsed.key === probeKey && typeof parsed.safe === 'boolean'
        ? { key: parsed.key, safe: parsed.safe }
        : null
  } catch {
    probe = null
  }
}

async function persistProbe(safe: boolean): Promise<void> {
  if (probeKey === null) return
  probe = { key: probeKey, safe }
  try {
    await Preferences.set({ key: PROBE_KEY, value: JSON.stringify(probe) })
  } catch {
    /* in-memory only */
  }
}

async function loadCachedConfig(): Promise<void> {
  if (config !== null) return
  try {
    const { value } = await Preferences.get({ key: CONFIG_KEY })
    const parsed = value ? (JSON.parse(value) as Partial<RemoteConfig>) : null
    if (parsed !== null && typeof parsed.enabled === 'boolean') config = { enabled: parsed.enabled }
  } catch {
    /* no cache */
  }
}

/**
 * Ask the server whether this surface is wanted. One request at a time; a
 * failure (offline, not yet authenticated) leaves whatever we knew before, so
 * a cold offline launch keeps the last answer and a first launch stays off.
 */
function refreshConfig(): Promise<void> {
  if (configInFlight !== null) return configInFlight
  configInFlight = (async () => {
    try {
      const res = await api.get<RemoteConfig>(configUrl(), { timeout: 5000 })
      const enabled = res.data?.enabled === true
      const wasEnabled = config?.enabled === true
      config = { enabled }
      try {
        await Preferences.set({ key: CONFIG_KEY, value: JSON.stringify(config) })
      } catch {
        /* in-memory only */
      }
      // The server switched us off: nothing already captured should go out.
      if (wasEnabled && !enabled) void purgeSamples()
      devLog(`config enabled=${enabled}`)
    } catch (e) {
      devLog('config fetch failed (keeping previous)', e)
    } finally {
      configInFlight = null
    }
  })()
  return configInFlight
}

/**
 * Wire the module up: cached config + probe, a background config refresh, and
 * the queue's lifecycle listeners. Idempotent; a no-op on a surface that
 * never captures. App.vue calls this on mount.
 */
export async function installVoiceQa(): Promise<void> {
  if (surface === null || installed) return
  installed = true
  await Promise.all([loadCachedConfig(), loadProbe()])
  void refreshConfig()
  void installSampleQueue()
}

/** Synchronous "would a capture run right now?". */
export function voiceQaMayCapture(): boolean {
  if (surface === null || engine === null || config?.enabled !== true || sessionDisabled)
    return false
  // The tee has no second microphone to probe for.
  if (engine === 'tee') return true
  return probe !== undefined && (probe === null || probe.safe)
}

/**
 * Whether the drill should open the microphone at the start of an exercise
 * for this module's sake (so the first tap has its pre-roll and contention is
 * resolved once, not per utterance). Only the parallel engine records through
 * the drill's microphone; on the tee the recogniser's plugin owns the only one,
 * and opening a second here is precisely the failure the tee exists to avoid.
 */
export function voiceQaNeedsDrillMic(): boolean {
  return engine === 'parallel' && voiceQaMayCapture()
}

/** Call at the start of each drill so the guards start fresh. */
export function resetVoiceQaSession(): void {
  sessionDisabled = false
  consecutiveFailures = 0
  consecutiveSilent = 0
}

export interface CaptureHandle {
  /**
   * The id this utterance's clip is filed under, minted here before anything
   * is recorded. Hand it to {@link reportHumanVerdict} if the therapist later
   * overrules the engine.
   */
  sampleId: string
  /**
   * The recogniser has answered. Cuts the clip and decides what to do with
   * it, entirely in the background — returns immediately.
   */
  finish(result: RecognitionResult, extra: { attemptIndex: number; target: string }): void
}

/** A fresh sample id: a v4 UUID, which is all the server accepts as a key. */
export function newSampleId(): string {
  return randomId()
}

/**
 * Whether each capture's clip actually went to the queue (true) or was
 * dropped on the way (false), by sample id, so a verdict for a clip that
 * never existed is not sent. Entries expire so the map cannot grow.
 */
const outcomes = new Map<string, Promise<boolean>>()
const OUTCOME_TTL_MS = 10 * 60 * 1000
/** How long a verdict waits for a clip that is still being cut. */
const OUTCOME_WAIT_MS = 3000

/**
 * Start capturing for one utterance. Resolves `null` whenever capture should
 * not happen — build/server/probe/session say no, the mic can't be opened,
 * the ring can't be marked — and never rejects. The recogniser is started by
 * the caller AFTER this resolves: on the tee that is what makes the plugin
 * pick the request up, on the parallel engine it is what lets the recogniser
 * win any contention the probe hasn't already ruled on.
 */
export async function beginCapture(target: string): Promise<CaptureHandle | null> {
  if (surface === null || engine === null) return null
  if (!installed) void installVoiceQa()
  if (config === null) {
    // Unknown yet (first launch before auth, or a launch that never reached
    // the server). Ask now for next time; this utterance goes uncaptured.
    void refreshConfig()
    return null
  }
  if (!voiceQaMayCapture()) return null

  let cut: () => Promise<RecordedClip>
  if (engine === 'tee') {
    // The recogniser's plugin keeps the audio; nothing is opened here.
    const clip = captureNextNativeUtterance()
    cut = async () => {
      const c = await clip
      if (c === null) throw new Error('no clip from the recogniser')
      return { blob: c.blob, ext: 'wav', mime: 'audio/wav' }
    }
  } else {
    try {
      const opened = await openMic()
      if (!opened) return null
      await startRecording()
    } catch (e) {
      devLog('beginCapture: recorder refused', e)
      return null
    }
    cut = () => stopRecording()
  }

  const sampleId = newSampleId()
  devLog(`capture started target="${target}" surface=${surface} engine=${engine} id=${sampleId}`)
  return {
    sampleId,
    finish(result, extra) {
      const outcome = finishCapture(sampleId, cut, result, extra).catch((e) => {
        logWarn('[voice-qa] finish failed:', e)
        return false
      })
      outcomes.set(sampleId, outcome)
      setTimeout(() => outcomes.delete(sampleId), OUTCOME_TTL_MS)
    },
  }
}

/** Resolves true once the clip is in the queue, false if it was dropped. */
async function finishCapture(
  sampleId: string,
  cut: () => Promise<RecordedClip>,
  result: RecognitionResult,
  extra: { attemptIndex: number; target: string },
): Promise<boolean> {
  if (surface === null || engine === null) return false

  let clip: RecordedClip
  try {
    clip = await cut()
  } catch {
    // Parallel: pause / close tore the capture down while the recogniser was
    // running. Tee: the session never started, or its clip never arrived.
    return false
  }

  const stats = clip.ext === 'wav' ? analyseWav(await clip.blob.arrayBuffer()) : null
  const rms = stats?.rms ?? null

  if (engine === 'parallel') {
    // 1. Device probe — once per device per version.
    if (probe === null) {
      const verdict = decideProbe({ status: result.status, transcript: result.transcript, rms })
      devLog(`probe verdict=${verdict} status=${result.status} rms=${rms}`)
      if (verdict === 'unsafe') {
        await persistProbe(false)
        closeMic()
        return false
      }
      if (verdict === 'safe') await persistProbe(true)
      // inconclusive: leave `probe` null and try again next time.
    }

    // 2. Running guard.
    if (recognitionFailedWithCapture(result, rms)) {
      consecutiveFailures++
      if (consecutiveFailures >= FAILURES_BEFORE_SESSION_OFF) {
        devLog('two recognitions failed with capture live — off for this session')
        sessionDisabled = true
        closeMic()
      }
      return false
    }
    consecutiveFailures = 0
  }

  // 3. Silence rejection — on both engines; a tee of a session that heard
  //    nothing is just as useless.
  if (rms !== null && rms < RMS_FLOOR) {
    consecutiveSilent++
    if (consecutiveSilent >= SILENT_CLIPS_BEFORE_REOPEN) {
      // Either the room is silent or the stream died under us; re-opening on
      // the next tap costs nothing and fixes the second case.
      consecutiveSilent = 0
      if (engine === 'parallel') closeMic()
    }
    devLog(`dropping silent clip rms=${rms}`)
    return false
  }
  consecutiveSilent = 0

  const info = await loadClientInfo()
  const meta: SampleMeta = {
    sample_id: sampleId,
    engine: engineName(),
    capture: engine,
    language: 'de-DE',
    target: extra.target,
    transcript: result.transcript,
    matched: result.matched,
    status: result.status,
    attempt_index: extra.attemptIndex,
    audio: {
      ...(stats !== null
        ? { sample_rate: stats.sampleRate, channels: stats.channels, duration_ms: stats.durationMs, rms: Number(stats.rms.toFixed(4)) }
        : {}),
    },
    client: info,
  }
  await enqueueSample(clip.blob, clip.mime, clip.ext, meta)
  return true
}

export type VerdictSurface = 'therapist-asr' | 'therapist-phoneme'

/**
 * The therapist overruled the engine on a sample: file the human verdict
 * next to the clip. Fire-and-forget and never throws — a lost verdict is a
 * missing label, not a broken drill, so there is no retry queue. Nothing is
 * sent on the tablet (no override exists there), in a dock (a docked session
 * never contributes), or for a clip this module knows it dropped.
 *
 * Not gated on this module's own `surface`: with the phoneme engine owning
 * the mic the surface is null here, yet the server-side tee still stored a
 * clip under the id the view handed to `evaluate()`.
 */
export function reportHumanVerdict(surface: VerdictSurface, sampleId: string, humanVerdict: boolean): void {
  if (!voiceQaBuildEnabled || isNative || isDocked) return
  void (async () => {
    try {
      const outcome = outcomes.get(sampleId)
      if (outcome !== undefined) {
        outcomes.delete(sampleId)
        const enqueued = await Promise.race([
          outcome,
          new Promise<boolean>((resolve) => setTimeout(() => resolve(true), OUTCOME_WAIT_MS)),
        ])
        if (!enqueued) {
          devLog(`verdict for ${sampleId} not sent: clip was dropped`)
          return
        }
      }
      const url = surface === 'therapist-phoneme' ? '/therapist/phoneme/verdict' : '/therapist/voice-samples/verdict'
      await api.post(url, { sample_id: sampleId, human_verdict: humanVerdict }, { timeout: 5000 })
      devLog(`verdict filed id=${sampleId} human_verdict=${humanVerdict}`)
    } catch (e) {
      devLog('verdict not sent', e)
    }
  })()
}
